import React, { useMemo, useState } from 'react';
import { base44 } from '@/api/base44Client';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import PageHeader from '@/components/ui/PageHeader';
import LoadingSpinner from '@/components/ui/LoadingSpinner';
import EmptyState from '@/components/ui/EmptyState';
import { motion } from 'framer-motion';
import { Users, Plus, Edit, Trash2, User, Calendar, Clock, MapPin, UserPlus, CheckCircle, X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { deliverMemberNotification } from '@/lib/notificationDelivery';

export default function AdminGrupos() {
  const navigate = useNavigate();
  const [showForm, setShowForm] = useState(false);
  const [editingGroup, setEditingGroup] = useState(null);
  const [selectedGroupRequests, setSelectedGroupRequests] = useState(null);
  const [selectedGroupMembers, setSelectedGroupMembers] = useState(null);
  const [searchMember, setSearchMember] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    leaders: [],
    is_active: true
  });

  const queryClient = useQueryClient();

  const { data: currentUser } = useQuery({
    queryKey: ['currentUser'],
    queryFn: () => base44.auth.me()
  });

  const { data: userPermissions } = useQuery({
    queryKey: ['userPermissions'],
    queryFn: async () => {
      const user = await base44.auth.me();
      const byUserId = await base44.entities.AdminPermission.filter({ user_id: user.id });
      if (byUserId.length > 0) return byUserId[0];
      const byUserEmail = await base44.entities.AdminPermission.filter({ user_email: user.email });
      if (byUserEmail.length > 0) return byUserEmail[0];
      const byEmail = await base44.entities.AdminPermission.filter({ email: user.email });
      return byEmail.length > 0 ? byEmail[0] : null;
    },
    enabled: !!currentUser
  });

  const { data: groups = [], isLoading } = useQuery({
    queryKey: ['adminGroups'],
    queryFn: () => base44.entities.Group.list('name', 100)
  });

  const { data: leaders = [] } = useQuery({
    queryKey: ['leaders'],
    queryFn: () => base44.entities.Leader.filter({ is_active: true })
  });

  const { data: allMembers = [] } = useQuery({
    queryKey: ['allMembers'],
    queryFn: () => base44.entities.Member.filter({ status: 'Ativo' }, 'full_name', 200)
  });

  const { data: groupMembers = [] } = useQuery({
    queryKey: ['groupMembers', selectedGroupMembers?.id],
    queryFn: async () => {
      if (!selectedGroupMembers?.member_ids?.length) return [];
      const memberPromises = selectedGroupMembers.member_ids.map(id => 
        base44.entities.Member.filter({ id })
      );
      const results = await Promise.all(memberPromises);
      return results.flat();
    },
    enabled: !!selectedGroupMembers
  });

  const canManageGroup = (group) => {
    if (currentUser?.role === 'admin' || userPermissions?.is_super_admin) return true;
    if (userPermissions?.is_super_admin) return true;
    if (userPermissions?.can_manage_groups) return true;
    // Leader pode aprovar solicitações do próprio grupo
    if (group.leader_id && leaders.some(l => l.id === group.leader_id && l.member_id === userPermissions?.user_id)) {
      return true;
    }
    return false;
  };

  const approveRequestMutation = useMutation({
    mutationFn: async ({ groupId, memberId, group }) => {
      const updatedRequests = (group.pending_requests || []).filter(r => r.member_id !== memberId);
      const updatedMembers = [...(group.member_ids || []), memberId];
      
      await base44.entities.Group.update(groupId, {
        pending_requests: updatedRequests,
        member_ids: updatedMembers
      });

      // Notificar membro sobre aprovação
      const member = allMembers.find(m => m.id === memberId);
      if (member) {
        // Criar notificação no app
        await deliverMemberNotification({
          recipients: [{ member_id: member.id, member_email: member.email }],
          title: 'Solicitação aprovada',
          message: `Sua solicitação para entrar no grupo ${group.name} foi aprovada!`,
          type: 'general',
          referenceId: groupId,
        });

        // Enviar email
        try {
          await base44.integrations.Core.SendEmail({
            to: member.email,
            subject: `Solicitação aprovada - ${group.name}`,
            body: `
              <h2>Parabéns!</h2>
              <p>Sua solicitação para entrar no grupo <strong>${group.name}</strong> foi aprovada.</p>
              <p>Agora você faz parte deste grupo e pode participar das atividades.</p>
            `
          });
        } catch (error) {
          console.error('Erro ao enviar email:', error);
        }

        // Enviar WhatsApp
        if (member.phone) {
          try {
            await base44.functions.invoke('sendGroupNotificationWhatsApp', {
              recipientPhone: member.phone,
              message: `✅ *Solicitação aprovada!*\n\nSua solicitação para entrar no grupo *${group.name}* foi aprovada.\n\nAgora você faz parte deste grupo! 🎉`
            });
          } catch (error) {
            console.error('Erro ao enviar WhatsApp:', error);
          }
        }
      }

      return { groupId, memberId, group };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['adminGroups'] });
      toast.success('Solicitação aprovada!');
    }
  });

  const rejectRequestMutation = useMutation({
    mutationFn: async ({ groupId, memberId, group }) => {
      const updatedRequests = (group.pending_requests || []).filter(r => r.member_id !== memberId);
      
      await base44.entities.Group.update(groupId, {
        pending_requests: updatedRequests
      });

      // Notificar membro sobre rejeição
      const member = allMembers.find(m => m.id === memberId);
      if (member) {
        // Criar notificação no app
        await deliverMemberNotification({
          recipients: [{ member_id: member.id, member_email: member.email }],
          title: 'Solicitação não aprovada',
          message: `Sua solicitação para entrar no grupo ${group.name} não foi aprovada no momento.`,
          type: 'general',
          referenceId: groupId,
        });

        // Enviar email
        try {
          await base44.integrations.Core.SendEmail({
            to: member.email,
            subject: `Solicitação de entrada - ${group.name}`,
            body: `
              <h2>Solicitação de Entrada no Grupo</h2>
              <p>Sua solicitação para entrar no grupo <strong>${group.name}</strong> não foi aprovada no momento.</p>
              <p>Entre em contato com a liderança para mais informações.</p>
            `
          });
        } catch (error) {
          console.error('Erro ao enviar email:', error);
        }

        // Enviar WhatsApp
        if (member.phone) {
          try {
            await base44.functions.invoke('sendGroupNotificationWhatsApp', {
              recipientPhone: member.phone,
              message: `ℹ️ *Solicitação de entrada*\n\nSua solicitação para entrar no grupo *${group.name}* não foi aprovada no momento.\n\nEntre em contato com a liderança para mais informações.`
            });
          } catch (error) {
            console.error('Erro ao enviar WhatsApp:', error);
          }
        }
      }

      return { groupId, memberId, group };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['adminGroups'] });
      toast.success('Solicitação rejeitada');
    }
  });

  const addMemberMutation = useMutation({
    mutationFn: async ({ groupId, memberId, group }) => {
      const currentMembers = group.member_ids || [];
      if (currentMembers.includes(memberId)) {
        throw new Error('Membro já está no grupo');
      }
      const updatedMembers = [...currentMembers, memberId];
      
      return base44.entities.Group.update(groupId, {
        member_ids: updatedMembers
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['adminGroups'] });
      queryClient.invalidateQueries({ queryKey: ['groupMembers'] });
      toast.success('Membro adicionado!');
      setSearchMember('');
    },
    onError: (error) => {
      toast.error(error.message || 'Erro ao adicionar membro');
    }
  });

  const removeMemberMutation = useMutation({
    mutationFn: async ({ groupId, memberId, group }) => {
      const updatedMembers = (group.member_ids || []).filter(id => id !== memberId);
      
      return base44.entities.Group.update(groupId, {
        member_ids: updatedMembers
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['adminGroups'] });
      queryClient.invalidateQueries({ queryKey: ['groupMembers'] });
      toast.success('Membro removido');
    }
  });

  const filteredAvailableMembers = allMembers.filter(member => {
    const isAlreadyInGroup = selectedGroupMembers?.member_ids?.includes(member.id);
    const matchesSearch = member.full_name.toLowerCase().includes(searchMember.toLowerCase());
    return !isAlreadyInGroup && matchesSearch;
  });
  const availableLeaders = useMemo(
    () => allMembers.filter((m) => !formData.leaders.some((l) => l.leader_id === m.id)),
    [allMembers, formData.leaders]
  );

  const createMutation = useMutation({
    mutationFn: (data) => base44.entities.Group.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['adminGroups'] });
      toast.success('Grupo criado com sucesso!');
      resetForm();
    }
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }) => base44.entities.Group.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['adminGroups'] });
      toast.success('Grupo atualizado!');
      resetForm();
    }
  });

  const deleteMutation = useMutation({
    mutationFn: (id) => base44.entities.Group.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['adminGroups'] });
      toast.success('Grupo excluído!');
    }
  });

  const resetForm = () => {
    setFormData({
      name: '',
      leaders: [],
      is_active: true
    });
    setEditingGroup(null);
    setShowForm(false);
  };

  const handleEdit = (group) => {
    setEditingGroup(group);
    setFormData({
      name: group.name || '',
      leaders: group.leaders || [],
      is_active: group.is_active ?? true
    });
    setShowForm(true);
  };

  const handleSubmit = () => {
    if (!formData.name) {
      toast.error('Nome é obrigatório.');
      return;
    }
    if (!formData.leaders || formData.leaders.length === 0) {
      toast.error('Adicione pelo menos um líder.');
      return;
    }

    const groupData = {
      name: formData.name,
      leaders: formData.leaders,
      is_active: formData.is_active,
      type: 'Outros',
      member_ids: [],
      pending_requests: []
    };

    if (editingGroup) {
      updateMutation.mutate({ id: editingGroup.id, data: formData });
    } else {
      createMutation.mutate(groupData);
    }
  };

  const addLeader = (memberId) => {
    const member = allMembers.find(m => m.id === memberId);
    if (!member) return;
    
    const alreadyAdded = formData.leaders.some(l => l.leader_id === memberId);
    if (alreadyAdded) {
      toast.info('Este líder já foi adicionado');
      return;
    }
    
    setFormData({
      ...formData,
      leaders: [...formData.leaders, { leader_id: member.id, leader_name: member.full_name }]
    });
  };

  const removeLeader = (leaderId) => {
    setFormData({
      ...formData,
      leaders: formData.leaders.filter(l => l.leader_id !== leaderId)
    });
  };

  const typeColors = {
    'Célula': 'bg-emerald-100 text-emerald-700',
    'Estudo Bíblico': 'bg-blue-100 text-blue-700',
    'Jovens': 'bg-purple-100 text-purple-700',
    'Oração': 'bg-rose-100 text-rose-700',
    'Visita': 'bg-amber-100 text-amber-700',
    'Ministério de Crianças': 'bg-pink-100 text-pink-700',
    'Reunião': 'bg-indigo-100 text-indigo-700',
    'Líderes': 'bg-slate-100 text-slate-700',
    'Louvor': 'bg-cyan-100 text-cyan-700',
    'Outros': 'bg-gray-100 text-gray-700',
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 p-4">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pb-24">
      <div className="bg-gradient-to-br from-sky-500 to-blue-600 text-white pt-8 pb-6 px-4">
        <div className="max-w-lg mx-auto">
          <PageHeader 
            title="Grupos" 
            subtitle={`${groups.length} grupos cadastrados`}
            showBack={true}
            action={
              <Button 
                size="sm" 
                variant="secondary"
                onClick={() => setShowForm(true)}
              >
                <Plus className="w-4 h-4 mr-1" />
                Novo
              </Button>
            }
          />
        </div>
      </div>

      <div className="max-w-lg mx-auto px-4 py-6">
        {groups.length === 0 ? (
          <EmptyState
            icon={Users}
            title="Nenhum grupo cadastrado"
            description="Crie grupos para a igreja."
            actionLabel="Criar Grupo"
            onAction={() => setShowForm(true)}
          />
        ) : (
          <div className="space-y-4">
            {groups.map((group, index) => (
              <motion.div
                key={group.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.03 }}
                className={`bg-white rounded-xl shadow-sm p-4 ${!group.is_active ? 'opacity-60' : ''}`}
              >
                <div className="flex items-start gap-3">
                  <div className="w-12 h-12 bg-sky-100 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Users className="w-6 h-6 text-sky-600" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <h3 className="font-semibold text-gray-900 truncate">{group.name}</h3>
                      {!group.is_active && (
                        <span className="text-xs bg-gray-100 px-2 py-0.5 rounded">Inativo</span>
                      )}
                    </div>
                    <div className="flex flex-wrap gap-2 mt-1">
                      <Badge className={`${typeColors[group.type] || typeColors['Outros']}`}>
                        {group.type}
                      </Badge>
                      {group.member_ids && group.member_ids.length > 0 && (
                        <Badge variant="outline" className="text-xs">
                          {group.member_ids.length} {group.member_ids.length === 1 ? 'membro' : 'membros'}
                        </Badge>
                      )}
                      {group.pending_requests && group.pending_requests.length > 0 && (
                        <Badge variant="outline" className="text-xs bg-amber-50 text-amber-700 border-amber-200">
                          {group.pending_requests.length} {group.pending_requests.length === 1 ? 'solicitação' : 'solicitações'}
                        </Badge>
                      )}
                    </div>
                    
                    {group.description && (
                      <p className="text-sm text-gray-600 mt-2 line-clamp-2">{group.description}</p>
                    )}
                    
                    {group.leaders && group.leaders.length > 0 && (
                      <div className="flex flex-wrap gap-2 mt-2">
                        {group.leaders.map((leader, idx) => (
                          <span key={idx} className="px-2 py-1 bg-indigo-100 text-indigo-700 text-xs rounded-full flex items-center gap-1">
                            <User className="w-3 h-3" />
                            {leader.leader_name}
                          </span>
                        ))}
                      </div>
                    )}
                    <div className="flex flex-wrap gap-3 mt-2 text-sm text-gray-500">
                      {group.meeting_day && (
                        <span className="flex items-center gap-1">
                          <Calendar className="w-3 h-3" />
                          {group.meeting_day}
                        </span>
                      )}
                      {group.meeting_time && (
                        <span className="flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {group.meeting_time}
                        </span>
                      )}
                    </div>
                    
                    {group.meeting_location && (
                      <div className="flex items-center gap-1 text-sm text-gray-500 mt-1">
                        <MapPin className="w-3 h-3" />
                        <span className="line-clamp-1">{group.meeting_location}</span>
                      </div>
                    )}
                  </div>
                </div>

                <div className="space-y-2 mt-4 pt-3 border-t">
                  {group.pending_requests && group.pending_requests.length > 0 && (
                    <Button
                      size="sm"
                      variant="outline"
                      className="w-full text-amber-600 border-amber-200"
                      onClick={() => setSelectedGroupRequests(group)}
                    >
                      <UserPlus className="w-4 h-4 mr-1" />
                      {group.pending_requests.length} {group.pending_requests.length === 1 ? 'Solicitação' : 'Solicitações'}
                    </Button>
                  )}
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => navigate(`/CalendarioGrupo?groupId=${group.id}`)}
                      title="Calendário do grupo"
                    >
                      <Calendar className="w-4 h-4" />
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      className="flex-1"
                      onClick={() => setSelectedGroupMembers(group)}
                    >
                      <Users className="w-4 h-4 mr-1" />
                      Membros ({group.member_ids?.length || 0})
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleEdit(group)}
                    >
                      <Edit className="w-4 h-4" />
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      className="text-red-600 border-red-200"
                      onClick={() => {
                        if (confirm('Excluir este grupo?')) {
                          deleteMutation.mutate(group.id);
                        }
                      }}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </div>

      {/* Form Dialog */}
      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-auto">
          <DialogHeader>
            <DialogTitle>
              {editingGroup ? 'Editar Grupo' : 'Novo Grupo'}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 mt-4">
            <div>
              <Label>Nome do Grupo *</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Ex: Célula Vida Nova"
              />
            </div>

            <div>
              <Label>Líderes do Grupo *</Label>
              
              {formData.leaders.length > 0 && (
                <div className="space-y-2 mb-3">
                  {formData.leaders.map((leader) => (
                    <div key={leader.leader_id} className="flex items-center justify-between p-3 bg-indigo-50 rounded-lg border border-indigo-200">
                      <span className="text-indigo-900 font-medium">{leader.leader_name}</span>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => removeLeader(leader.leader_id)}
                        className="text-red-600 hover:text-red-700"
                      >
                        Remover
                      </Button>
                    </div>
                  ))}
                </div>
              )}

              <Select onValueChange={addLeader}>
                <SelectTrigger>
                  <SelectValue placeholder="Adicionar líder..." />
                </SelectTrigger>
                <SelectContent>
                  {availableLeaders.map((member) => (
                    <SelectItem key={member.id} value={member.id}>
                      <div className="flex items-center gap-2">
                        {member.photo_url ? (
                          <img
                            src={member.photo_url}
                            alt={member.full_name}
                            className="w-6 h-6 rounded-full object-cover border border-gray-200"
                          />
                        ) : (
                          <div className="w-6 h-6 rounded-full bg-sky-100 text-sky-700 border border-sky-200 flex items-center justify-center text-xs font-semibold">
                            {member.full_name?.charAt(0)?.toUpperCase() || 'M'}
                          </div>
                        )}
                        <span>{member.full_name}</span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="flex items-center justify-between">
              <Label>Grupo ativo</Label>
              <Switch
                checked={formData.is_active}
                onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })}
              />
            </div>

            <div className="flex gap-3 pt-4">
              <Button variant="outline" className="flex-1" onClick={resetForm}>
                Cancelar
              </Button>
              <Button 
                className="flex-1" 
                onClick={handleSubmit}
                disabled={createMutation.isPending || updateMutation.isPending}
              >
                {editingGroup ? 'Salvar' : 'Criar'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Requests Dialog */}
      <Dialog open={!!selectedGroupRequests} onOpenChange={() => setSelectedGroupRequests(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Solicitações - {selectedGroupRequests?.name}</DialogTitle>
          </DialogHeader>

          <div className="space-y-3 mt-4">
            {selectedGroupRequests?.pending_requests?.length === 0 ? (
              <p className="text-center text-gray-500 py-8">Nenhuma solicitação pendente</p>
            ) : (
              selectedGroupRequests?.pending_requests?.map((request) => (
                <div key={request.member_id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div>
                    <p className="font-medium text-gray-900">{request.member_name}</p>
                    <p className="text-xs text-gray-500">
                      {new Date(request.requested_at).toLocaleDateString('pt-BR')}
                    </p>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      className="text-green-600 border-green-200"
                      onClick={() => approveRequestMutation.mutate({
                        groupId: selectedGroupRequests.id,
                        memberId: request.member_id,
                        group: selectedGroupRequests
                      })}
                      disabled={approveRequestMutation.isPending}
                    >
                      <CheckCircle className="w-4 h-4" />
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      className="text-red-600 border-red-200"
                      onClick={() => rejectRequestMutation.mutate({
                        groupId: selectedGroupRequests.id,
                        memberId: request.member_id,
                        group: selectedGroupRequests
                      })}
                      disabled={rejectRequestMutation.isPending}
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Members Management Dialog */}
      <Dialog open={!!selectedGroupMembers} onOpenChange={() => {
        setSelectedGroupMembers(null);
        setSearchMember('');
      }}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-auto">
          <DialogHeader>
            <DialogTitle>Membros - {selectedGroupMembers?.name}</DialogTitle>
          </DialogHeader>

          <div className="space-y-4 mt-4">
            {/* Add Member Section */}
            <div>
              <Label>Adicionar Membro</Label>
              <div className="relative mt-2">
                <Input
                  placeholder="Buscar membro..."
                  value={searchMember}
                  onChange={(e) => setSearchMember(e.target.value)}
                />
                {searchMember && filteredAvailableMembers.length > 0 && (
                  <div className="absolute z-10 w-full mt-1 bg-white border rounded-lg shadow-lg max-h-48 overflow-auto">
                    {filteredAvailableMembers.slice(0, 10).map((member) => (
                      <button
                        key={member.id}
                        className="w-full text-left px-4 py-2 hover:bg-gray-50 transition-colors"
                        onClick={() => addMemberMutation.mutate({
                          groupId: selectedGroupMembers.id,
                          memberId: member.id,
                          group: selectedGroupMembers
                        })}
                      >
                        <div className="flex items-center gap-2">
                          {member.photo_url ? (
                            <img
                              src={member.photo_url}
                              alt={member.full_name}
                              className="w-7 h-7 rounded-full object-cover border border-gray-200"
                            />
                          ) : (
                            <div className="w-7 h-7 rounded-full bg-sky-100 text-sky-700 border border-sky-200 flex items-center justify-center text-xs font-semibold">
                              {member.full_name?.charAt(0)?.toUpperCase() || 'M'}
                            </div>
                          )}
                          <div className="min-w-0">
                            <p className="font-medium text-gray-900 truncate">{member.full_name}</p>
                            <p className="text-xs text-gray-500 truncate">{member.email}</p>
                          </div>
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Current Members List */}
            <div>
              <Label>Membros Atuais ({groupMembers.length})</Label>
              <div className="space-y-2 mt-2">
                {groupMembers.length === 0 ? (
                  <p className="text-center text-gray-500 py-8">Nenhum membro no grupo</p>
                ) : (
                  groupMembers.map((member) => (
                    <div key={member.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <div className="flex-1">
                        <p className="font-medium text-gray-900">{member.full_name}</p>
                        <p className="text-xs text-gray-500">{member.email}</p>
                      </div>
                      <Button
                        size="sm"
                        variant="outline"
                        className="text-red-600 border-red-200"
                        onClick={() => {
                          if (confirm(`Remover ${member.full_name} do grupo?`)) {
                            removeMemberMutation.mutate({
                              groupId: selectedGroupMembers.id,
                              memberId: member.id,
                              group: selectedGroupMembers
                            });
                          }
                        }}
                        disabled={removeMemberMutation.isPending}
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
